import type {DefaultError, UseQueryOptions} from "@tanstack/react-query";
import type {UrlQueryKey} from "../../../query/queryClient";
import {useCustomQuery} from "../../../query/queryClient";
import {queryHttp} from "../../../query/queryHttp";
import {CHAT_CONFIG} from "../services/ChatConfiguration";
import {agentsMetadataUrl} from "../services/chatAgentApiUrls";
import type {AgentMetadata} from "../types/chatTypes";

/**
 * Shared React Query key for `GET …/agents-metadata`.
 * Same key everywhere so the response is cached once.
 */
export const AGENTS_METADATA_QUERY_KEY = [
    "/chat-agent/agents-metadata",
] as const satisfies UrlQueryKey;

/** One fetch per app load; refetch only on manual invalidation. */
export const AGENTS_METADATA_STALE_TIME_MS = Number.POSITIVE_INFINITY;

type AgentsMetadataQueryKey = typeof AGENTS_METADATA_QUERY_KEY;

/**
 * Cached `GET …/agents-metadata` via React Query (shared key → one network request).
 * Re-use anywhere under `chat-agent/`; override options only when needed (e.g. `enabled`).
 */
export function useAgentMetadata(
    options?: Omit<
    UseQueryOptions<
      AgentMetadata[],
      DefaultError,
      AgentMetadata[],
      AgentsMetadataQueryKey
    >,
    "queryKey" | "queryFn"
  >,
) {
    return useCustomQuery<
    AgentMetadata[],
    DefaultError,
    AgentMetadata[],
    AgentsMetadataQueryKey
  >({
        queryKey: AGENTS_METADATA_QUERY_KEY,
        queryFn: async () => {
            if (!CHAT_CONFIG.API_URL) {
                return [];
            }
            const {data} = await queryHttp.get<AgentMetadata[]>(agentsMetadataUrl());
            return Array.isArray(data) ? data : [];
        },
        staleTime: AGENTS_METADATA_STALE_TIME_MS,
        ...options,
    });
}
